import { Box, Radio, Typography, useTheme } from '@mui/material';
import React, { memo, useState, useImperativeHandle, forwardRef } from 'react';

import DebitCard from './imgs/debitCard';
import CreditCard from './imgs/creditCard';

import { PaymentForm } from '@shared/components/PaymentForm';
import GenericDropdown from '@shared/components/GenericDropdown';
import FormSection from '@shared/components/FormSection';

import useWindowSize from '@shared/hooks/useWindowsSize';
import { usePaymentMethod } from './usePaymentMethod';

import { sxStyle } from './styles';

/* =========================
   Subcomponente UI (sin lógica)
========================= */
const PaymentOption = ({
  checked,
  value,
  label,
  Icon,
  onRadioChange,
  onBoxClick,
  error,
  screenSize,
  theme,
}) => {
  const isMobile = screenSize.width < 600;

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
      <Radio
        checked={checked}
        onChange={onRadioChange}
        value={value}
        name="radio-button-demo"
        size={isMobile ? 'small' : 'medium'}
        sx={{
          color: error ? theme.palette.error.main : 'black',
          '&.Mui-checked': { color: theme.tertiary },
          mt: 0.5,
        }}
      />

      <Box
        sx={checked ? sxStyle.cardBoxSelected(theme) : sxStyle.cardBox}
        onClick={onBoxClick}
      >
        <Icon
          width={isMobile ? 32 : 52}
          height={isMobile ? 26 : 39}
          color={checked ? '#FFFFFF' : '#E5E5E5'}
        />
        <Typography sx={checked ? sxStyle.cardTextSelected : sxStyle.cardText}>
          {label}
        </Typography>
      </Box>
    </Box>
  );
};

/* =========================
   Componente principal
========================= */
const PaymentMethodSelector = (
  {
    handleChangeInfoCard,
    precio,
    setCardData,
    cardData,
    idPlan,
    isInUSD = false,
    pagoValue,
    setPriceDetail,
    formikPaymentRef,
  },
  ref
) => {
  const screenSize = useWindowSize();
  const theme = useTheme();

  const [errors, setErrors] = useState({
    method: false,
    card: false,
    cuotas: false,
  });

  const [openDropdown, setOpenDropdown] = useState(null);

  const {
    selectedValueRadio,
    handleChangeRadio,
    mediosDePago,
    pagoDeb,
    pagoCred,
    pagoCuot,
    setPagoCuot,
    pagoPrisma,
    handleChangeCrd,
    handleSelectDebito,
    handleChangeCuot,
    handleBoxClick,
    debitoItems,
    creditoItems,
    creditoDropdownItems,
    setCardType,
    setPagoCred,
    setPagoDeb,
  } = usePaymentMethod({
    precio,
    idPlan,
    isInUSD,
    setCardData,
    handleChangeInfoCard,
    cardData,
    pagoValue,
    setPriceDetail,
  });

  /* =========================
     VALIDACIÓN (idéntica lógica)
  ========================= */
  const validatePayment = () => {
    let valid = true;

    const newErrors = {
      method: false,
      card: false,
      cuotas: false,
    };

    if (!selectedValueRadio) {
      newErrors.method = true;
      valid = false;
    }

    if (selectedValueRadio === 'debito' && !pagoDeb) {
      newErrors.card = true;
      valid = false;
    }

    if (selectedValueRadio === 'credito' && !pagoCred) {
      newErrors.card = true;
      valid = false;
    }

    if (selectedValueRadio === 'credito' && !pagoCuot) {
      newErrors.cuotas = true;
      valid = false;
    }

    setErrors(newErrors);

    return valid;
  };

  useImperativeHandle(ref, () => ({
    validatePayment,
  }));

  if (!mediosDePago) return null;

  /* =========================
     CONFIG DROPDOWN (igual)
  ========================= */
  let dropdownConfig = null;

  let noPaymentOptions = false;

  if (selectedValueRadio === 'debito') {
    if (debitoItems?.length > 0) {
      dropdownConfig = {
        items: debitoItems,
        selectedItem: pagoDeb,
        onSelect: item => {
          handleSelectDebito(item);
          setErrors(prev => ({ ...prev, card: false }));
        },
        buttonLabel: 'Seleccioná la tarjeta',
        iconWidth: 65,
        menuItemHeight: 45,
        formControlHeight: 45,
        formControlHeightMobile: 95,
      };
    } else {
      noPaymentOptions = true;
    }
  }

  if (selectedValueRadio === 'credito') {
    if (creditoItems?.length > 0) {
      dropdownConfig = {
        items: creditoItems,
        selectedItem: pagoCred,
        onSelect: item => {
          handleChangeCrd(item);
          setErrors(prev => ({ ...prev, card: false }));
        },
        buttonLabel: 'Seleccioná tu plan de cuotas',
        iconWidth: 65,
        menuItemHeight: 45,
        formControlHeight: 45,
        formControlHeightMobile: 95,
      };
    } else {
      noPaymentOptions = true;
    }
  }
  /* =========================
     HANDLERS ORIGINALES (sin tocar flujo)
  ========================= */

  const localHandleChangeRadio = e => {
    setErrors({ method: false, card: false, cuotas: false });
    handleChangeRadio(e.target.value);
  };

  const handleDebitoRadio = e => {
    localHandleChangeRadio(e);
    setCardType(e.target.value);
    setPagoCred(null);
    setPagoCuot(null);
    setCardData(undefined);
    handleChangeInfoCard(null);
  };

  const handleCreditoRadio = e => {
    localHandleChangeRadio(e);
    setCardType(e.target.value);
    setPagoDeb(null);
    setCardData(undefined);
    handleChangeInfoCard(null);
  };

  const handleDebitoBox = () => {
    handleBoxClick('debito');
    setErrors(prev => ({ ...prev, method: false }));

    setCardType('debito');
    setPagoCred(null);
    setPagoCuot(null);
    setCardData(undefined);
    handleChangeInfoCard(null);
  };

  const handleCreditoBox = () => {
    handleBoxClick('credito');
    setErrors(prev => ({ ...prev, method: false }));

    setCardType('credito');
    setPagoDeb(null);
    setCardData(undefined);
    handleChangeInfoCard(null);
  };

  const showPaymentForm = pagoDeb || (pagoCred && pagoCuot);

  return (
    <FormSection
      title={'Seleccioná el método de pago'}
      centerChildren
      wrapItems={false}
      customLayout={{ xs: { 1: 12, 2: 12, 3: 12, 4: 12 } }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: { xs: 2, sm: 3 },
          py: { xs: '20px', sm: '40px' },
        }}
      >
        <PaymentOption
          value="debito"
          label="Tarjeta de débito"
          Icon={DebitCard}
          checked={selectedValueRadio === 'debito'}
          error={errors.method}
          screenSize={screenSize}
          theme={theme}
          onRadioChange={handleDebitoRadio}
          onBoxClick={handleDebitoBox}
        />

        <PaymentOption
          value="credito"
          label="Tarjeta de crédito"
          Icon={CreditCard}
          checked={selectedValueRadio === 'credito'}
          error={errors.method}
          screenSize={screenSize}
          theme={theme}
          onRadioChange={handleCreditoRadio}
          onBoxClick={handleCreditoBox}
        />
      </Box>

      {noPaymentOptions && (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Typography color="text.secondary">
            Por el momento, no contamos con este método de pago. Por favor,
            seleccioná otro.
          </Typography>
        </Box>
      )}

      {dropdownConfig && (
        <Box sx={{ width: { xs: '100%', lg: '65%' }, mx: 'auto' }}>
          <GenericDropdown
            {...dropdownConfig}
            isOpen={openDropdown === 'card'}
            setIsOpen={value => setOpenDropdown(value ? 'card' : null)}
          />

          {errors.card && (
            <Typography color="error" variant="caption">
              Debes seleccionar una tarjeta
            </Typography>
          )}
        </Box>
      )}

      {pagoCred && (
        <Box sx={{ width: { xs: '100%', lg: '65%' }, mx: 'auto' }}>
          <GenericDropdown
            items={creditoDropdownItems}
            selectedItem={pagoCuot}
            onSelect={item => {
              handleChangeCuot(item);
              setErrors(prev => ({ ...prev, cuotas: false }));
              setOpenDropdown(null);
            }}
            isOpen={openDropdown === 'cuotas'}
            setIsOpen={value => setOpenDropdown(value ? 'cuotas' : null)}
            buttonLabel="Seleccioná la tarjeta"
            iconWidth={65}
            menuItemHeight={45}
            formControlHeight={45}
            formControlHeightMobile={95}
          />

          {errors.cuotas && (
            <Typography color="error" variant="caption">
              Debes seleccionar un plan de cuotas
            </Typography>
          )}
        </Box>
      )}

      {showPaymentForm ? (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <PaymentForm
            key={selectedValueRadio}
            paymentValues={obj => pagoPrisma(obj)}
            formikRef={formikPaymentRef}
          />
        </Box>
      ) : (
        <Box sx={{ py: 1 }} />
      )}
    </FormSection>
  );
};

export default memo(forwardRef(PaymentMethodSelector));
